import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SHOW_ESIM, _adr } from "../../../address";
import { complexDigitToman } from "../../../tools/tools";
import { Roles } from "../../../tools/services";
import { isBlockedPath, isImpersonating } from "../../../tools/impersonation";
import { useLayout } from "../../layouts/layout";

// most-used sales flows, shown only when the dealer holds the role (and not while impersonating)
const ACTIONS = [
	{ label: "ثبت‌نام سیم‌کارت", icon: "icon-simcard", role: Roles.Dealer_RegisterSim_B2C_Iranian, path: _adr.SimCardPurchase.Index + _adr.SimCardPurchase.Kit },
	SHOW_ESIM && { label: "فروش eSIM", icon: "icon-simcard", role: [Roles.Dealer_RegisterSim_B2C_Iranian, Roles.Dealer_ESIM_Sales], path: _adr.SimCardPurchase.Index + _adr.SimCardPurchase.Esim.Bundle },
	{ label: "فروش شارژ", icon: "icon-dc", role: Roles.Dealer_DCPurchase_B2C, path: _adr.DCPurchase.Index + _adr.DCPurchase.Shop + _adr.DCPurchase.Charge },
	{ label: "فروش بسته", icon: "icon-packages", role: Roles.Dealer_EPPurchase_B2C, path: _adr.EPPurchase.Index + _adr.EPPurchase.Shop + _adr.EPPurchase.Valid },
	{
		label: "تعویض سیم‌کارت",
		icon: "icon-change-sim",
		role: Roles.Dealer_SimSwap_B2C_Iranian,
		path: _adr.SimCardReplacement.Index + _adr.SimCardReplacement.Replacement + _adr.SimCardReplacement.ValidMsisdn,
	},
	{ label: "خدمات ۳۶۰", icon: "icon-service360", role: Roles.Delear_360_Customerservice, path: _adr.Services360.Index },
];

const greeting = (h = new Date().getHours()) => (h < 12 ? "صبح بخیر" : h < 17 ? "روز بخیر" : "عصر بخیر");
// "یکشنبه ۱۹ مهر ۱۴۰۵" — built from parts because Intl's own fa ordering comes out as "۱۴۰۵ مهر ۱۹, یکشنبه"
const today = () => {
	const parts = new Intl.DateTimeFormat("fa-IR-u-ca-persian", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).formatToParts(new Date());
	const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
	return `${get("weekday")} ${get("day")} ${get("month")} ${get("year")}`;
};

// today's calendar events (pnldev.com: free, no key, CORS open). Read once a day and kept until the day ends;
// any failure just shows nothing.
const EVENTS_KEY = "_day_events";
const useTodayEvents = () => {
	const [day, setDay] = useState(null);
	useEffect(() => {
		const p = Object.fromEntries(
			new Intl.DateTimeFormat("en-u-ca-persian-nu-latn", { year: "numeric", month: "numeric", day: "numeric" })
				.formatToParts(new Date())
				.map((x) => [x.type, x.value]),
		);
		const id = `${p.year}/${p.month}/${p.day}`;
		try {
			const cached = JSON.parse(localStorage.getItem(EVENTS_KEY));
			if (cached?.id === id) return setDay(cached);
		} catch {}
		const ctrl = new AbortController();
		const timer = setTimeout(() => ctrl.abort(), 6000);
		fetch(`https://pnldev.com/api/calender?year=${p.year}&month=${p.month}&day=${p.day}`, { signal: ctrl.signal })
			.then((r) => (r.ok ? r.json() : null))
			.then((j) => {
				if (!j?.status || !j.result) return;
				const value = { id, holiday: !!j.result.holiday, events: (j.result.event ?? []).filter((e) => typeof e === "string" && e.trim()) };
				setDay(value);
				try {
					localStorage.setItem(EVENTS_KEY, JSON.stringify(value));
				} catch {}
			})
			.catch(() => {})
			.finally(() => clearTimeout(timer));
		return () => {
			clearTimeout(timer);
			ctrl.abort();
		};
	}, []);
	return day;
};

// the one motion moment on the page: balances count up once on load
const useCountUp = (target = 0, ms = 1100) => {
	const [value, setValue] = useState(0);
	useEffect(() => {
		const end = Number(target) || 0;
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return setValue(end);
		let frame;
		const start = performance.now();
		const tick = (now) => {
			const p = Math.min(1, (now - start) / ms);
			setValue(end * (1 - Math.pow(2, -10 * p))); // ease-out expo
			if (p < 1) frame = requestAnimationFrame(tick);
			else setValue(end);
		};
		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [target, ms]);
	return value;
};

const Hero = ({ user, popup, onOpenPopup }) => {
	const nav = useNavigate();
	const { pendingDocs } = useLayout();
	const roles = user?.roles ?? [];

	// separate package/charge wallets only when both carry a balance; otherwise the single wallet amount
	const split = !!user?.ccwalletamount && !!user?.cpwalletamount;
	const main = useCountUp(split ? user.cpwalletamount : user?.walletAmount);
	const charge = useCountUp(split ? user.ccwalletamount : 0);

	const actions = ACTIONS.filter((a) => a && [].concat(a.role).every((r) => roles.includes(r)) && !(isImpersonating() && isBlockedPath(_adr.Root + a.path)));
	const pending = pendingDocs?.TotalCount ?? 0;
	const todayEvents = useTodayEvents();

	return (
		<section className="dash-hero" aria-labelledby="hero-title">
			<img className="hero-decor" src={process.env.PUBLIC_URL + process.env.REACT_APP_PATH + "/assets/images/logo-small.png"} alt="" aria-hidden="true" />

			<div className="hero-main">
				<p className="hero-date">{today()}</p>
				{(todayEvents?.events.length > 0 || todayEvents?.holiday) && (
					<p className="hero-events">
						{todayEvents.holiday && <span className="hero-holiday">تعطیل رسمی</span>}
						{todayEvents.events.join(" · ")}
					</p>
				)}
				<h1 id="hero-title">
					{greeting()}
					{user?.firstname ? `، ${user.firstname}` : ""}
				</h1>
				{pending > 0 ? (
					<p className="hero-alert">
						<span className="hero-alert-dot" aria-hidden="true" />
						<strong>{pending}</strong> درخواست منتظر تکمیل مدارک است
					</p>
				) : (
					// only claim "all complete" once the service actually answered (null = still loading or failed)
					pendingDocs && <p className="hero-sub">همه‌ی درخواست‌ها مدارک کامل دارند.</p>
				)}
				{popup && (
					<button type="button" className="hero-chip" onClick={onOpenPopup}>
						{popup.title}
					</button>
				)}
			</div>

			<div className="hero-wallet">
				<span className="hero-label">{split ? "کیف پول بسته" : "اعتبار کیف پول"}</span>
				<p className="hero-balance">
					<span dir="ltr">{complexDigitToman(Math.round(main))}</span> <small>تومان</small>
				</p>
				{split && (
					<p className="hero-balance hero-balance-sm">
						<span className="hero-label">کیف پول شارژ</span>
						<span dir="ltr">{complexDigitToman(Math.round(charge))}</span> <small>تومان</small>
					</p>
				)}
				{roles.includes(Roles.Dealer_Wallet_Management) && (
					<button type="button" className="hero-wallet-btn" onClick={() => nav(_adr.Root + _adr.Wallet.Index + _adr.Wallet.WalletList)}>
						<i className="icon-wallet" aria-hidden="true"></i> مدیریت کیف پول
					</button>
				)}
			</div>

			{actions.length > 0 && (
				<nav className="hero-actions" aria-label="دسترسی سریع">
					{actions.map((a) => (
						<button type="button" key={a.label} onClick={() => nav(_adr.Root + a.path)}>
							<i className={a.icon} aria-hidden="true"></i>
							{a.label}
						</button>
					))}
				</nav>
			)}
		</section>
	);
};

export default Hero;
